import { Box, HStack, useDisclosure } from '@chakra-ui/react';
import { BackofficeOrderDetail } from '@diamond/shared/types';
import {
  Breadcrumbs,
  Button,
  Divider,
  LoadingOverlay,
  PageLoader,
  Text,
} from '@diamond/shared/ui';
import { adminCaptureException, LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';
import { AdminLayout } from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useActivitiesDetail, useCancelReason } from '../api';
import {
  ActivityActionProvider,
  useActivityAction,
} from '../components/activity-detail/action-context';
import { ActivityDetailActionBar } from '../components/activity-detail/activity-detail-action-bar';
import { ActivityDetailItemsTable } from '../components/activity-detail/activity-detail-items-table';
import { ActivityDetailPromoVoucherSection } from '../components/activity-detail/activity-detail-promo-voucher-section';
import { AddProductModal } from '../components/activity-detail/modals/add-product-modal';
import { ActivitiesDetailnfo } from '../components/activity-detail-info';
import {
  ActivityDetailErrorBoundary,
  ActivityDetailTableErrorBoundary,
} from '../components/error-boundaries';
import { OrderHistoryModal } from '../components/order-history-modal';

export function ActivitiesDetailPage() {
  const params = useParams();
  const activitiesId = params['activitiesId'] as string;
  const methods = useForm();
  const session = useAuthStore();

  const links = [
    {
      title: 'Beranda',
      link: '/',
    },
    {
      title: 'Activities',
      link: '/activities',
    },
    {
      title: 'Detail',
      link: `/activities/${activitiesId}`,
    },
  ];

  const { cancelReasonData } = useCancelReason({
    size: 50,
    page: 1,
  });

  const headerQueryString = { order_by: '', direction: '', size: 15, page: 1 };

  // Targeted header slice
  const selectHeader = useCallback(
    (d: BackofficeOrderDetail) => ({
      status: d.order.status,
      count_items: d.order.count_items,
      sap_note: d.order.sap_note,
      invoice_link: d.order.invoice_link,
      delivery_link: d.order.delivery_link,
      cancel_reason_id: d.order.cancel_reason_id,
      division: d.order.division,
      total: d.total,
    }),
    []
  );

  // ============================================
  // REAL API SECTION - Comment this entire block to use MOCK API below
  // ============================================
  const { activitiesDetailData, isLoading, refetch } = useActivitiesDetail(
    activitiesId,
    headerQueryString
  );

  const { activitiesDetailData: headerData } = useActivitiesDetail(
    activitiesId,
    headerQueryString,
    { select: selectHeader }
  );
  // ============================================
  // END REAL API SECTION
  // ============================================

  // ============================================
  // MOCK API SECTION - Uncomment this entire block and comment REAL API section above to use MOCK API
  // ============================================
  // const {
  //   mockOrderDetail,
  //   isLoading: isLoadingMock,
  //   isFetching: isFetchingMock,
  //   refetch: refetchMock,
  // } = useMockActivityVoucherData(activitiesId);

  // const activitiesDetailData = mockOrderDetail;
  // const isLoading = isLoadingMock;
  // const refetch = refetchMock;
  // const headerData = mockOrderDetail
  //   ? selectHeader(mockOrderDetail)
  //   : undefined;
  // ============================================
  // END MOCK API SECTION
  // ============================================

  const cancelReasonId = headerData?.cancel_reason_id;
  const isVoucherMenuEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_VOUCHERS_MENU,
    false
  );
  const cancelReason = (
    cancelReasonId
      ? cancelReasonData?.cancelReasonMap[cancelReasonId].reason
      : ''
  ) as string;

  const {
    isOpen: isOpenAddProduct,
    onOpen: onOpenAddProduct,
    onClose: onCloseAddProduct,
  } = useDisclosure();

  const {
    isOpen: isOpenOrderHistory,
    onOpen: onOpenOrderHistory,
    onClose: onCloseOrderHistory,
  } = useDisclosure();

  const filteredCancelOrder = cancelReasonData
    ? cancelReasonData?.data.filter(
        (value) => value.reason_type.toLowerCase() === 'order'
      )
    : [];

  const filteredCancelItem = cancelReasonData
    ? cancelReasonData?.data.filter(
        (value) => value.reason_type.toLowerCase() === 'item'
      )
    : [];

  const cancelReasonPerOrder = filteredCancelOrder.map((value) => {
    return {
      label: value.reason,
      value: value.reason_id,
    };
  });

  const cancelReasonPerItem = [
    { label: '', value: '' },
    ...filteredCancelItem.map((value) => {
      return {
        label: value.reason,
        value: value.reason_id,
      };
    }),
  ];
  const statusData = headerData?.status;
  const isDisable = statusData !== 'submitted' ? true : false;

  if (isLoading) {
    return (
      <AdminLayout>
        <PageLoader />
      </AdminLayout>
    );
  }

  if (!activitiesDetailData) {
    adminCaptureException(
      {
        message: 'Failed to load Activity Detail data',
        error: 'API_DATA_UNAVAILABLE',
        isLoading,
        hasData: !!activitiesDetailData,
        activitiesId,
      },
      (scope) => {
        scope.setTags({
          page: 'activity-detail',
          errorType: 'api-error',
          activitiesId: activitiesId,
        });
        scope.setUser({
          id: activitiesId,
          email: session.userEmail,
        });
        scope.setContext('error', {
          message: 'Order detail data is undefined',
          isLoading,
          hasData: !!activitiesDetailData,
        });
        scope.setLevel('error');
      }
    );

    return (
      <AdminLayout>
        <Box p={8} bg="white" borderRadius="md" shadow="sm">
          <Box textAlign="center" py={10}>
            <Text fontSize="2xl" fontWeight="bold" color="red.500" mb={4}>
              Gagal Memuat Detail Pesanan
            </Text>
            <Text color="gray.600" mb={6}>
              Terjadi kesalahan saat mengambil data pesanan. Silakan coba lagi
              atau hubungi tim support jika masalah berlanjut.
            </Text>
            <Button colorScheme="blue" onClick={() => window.location.reload()}>
              Muat Ulang Halaman
            </Button>
          </Box>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout maxWidth="full">
      <ActivityDetailErrorBoundary>
        <Box paddingBottom={4}>
          <Breadcrumbs links={links} useRouterDom />
        </Box>
        {/* Submit loading overlay handled in SubmitButton */}
        <FormProvider {...methods}>
          <ActivityActionProvider>
            <ActivityBlockingOverlay />
            <Box>
              <HStack
                direction="row"
                justifyContent="space-between"
                verticalAlign="center"
              >
                <Text fontSize="xl" fontWeight="bold">
                  Activity Detail
                </Text>
              </HStack>
              <Divider />
              <HStack my="3" spacing="4" direction="row" alignItems="center">
                <Button
                  variant="ghost"
                  onClick={onOpenAddProduct}
                  display={statusData === 'submitted' ? 'flex' : 'none'}
                  data-testid="add-more-product"
                >
                  Tambah Produk
                </Button>

                <ActivityDetailActionBar
                  activitiesId={activitiesId}
                  queryString={headerQueryString}
                  cancelReason={cancelReason}
                  onOpenOrderHistory={onOpenOrderHistory}
                  register={methods.register}
                  refetch={refetch}
                />
              </HStack>
            </Box>
            <Divider />
            <ActivitiesDetailnfo
              data={activitiesDetailData}
              cancelReasonData={cancelReasonPerOrder}
              refetch={refetch}
            />
            <Divider />
            <ActivityDetailTableErrorBoundary>
              <ActivityDetailItemsTable
                activitiesId={activitiesId}
                cancelReasonPerItem={cancelReasonPerItem}
                isDisable={isDisable}
              />
            </ActivityDetailTableErrorBoundary>
            {/* Promo & Voucher section */}
            {isVoucherMenuEnabled ? (
              <ActivityDetailPromoVoucherSection
                orderId={activitiesId}
                orderPromos={activitiesDetailData?.order.promos}
              />
            ) : null}
            <Divider />
            {/* Submit and Cancel modals are rendered by their respective button components */}
            <AddProductModal
              isOpen={isOpenAddProduct}
              onClose={onCloseAddProduct}
              activitiesId={activitiesId}
              division={headerData?.division}
              refetch={refetch}
            />
            <OrderHistoryModal
              isOpen={isOpenOrderHistory}
              onClose={onCloseOrderHistory}
              selectedDataId={activitiesId}
            />
          </ActivityActionProvider>
        </FormProvider>
      </ActivityDetailErrorBoundary>
    </AdminLayout>
  );
}

function ActivityBlockingOverlay() {
  const { blockUI, blockUIText } = useActivityAction();
  return blockUI ? (
    <LoadingOverlay isLoading={true} text={blockUIText} />
  ) : null;
}
